import React, { useCallback, useEffect, useState } from "react";

// WAVE 3 replaces these one at a time. A step listed here is v2's own; a step
// still imported above is v1's, mounted behind this shell until its turn.
//   1. Psychology Map — ported 2026-09-09
//   2. Angle         — ported 2026-09-09
//   3. Hooks         — ported 2026-09-09
//   4. Script        — ported 2026-09-09
//   5. CTA           — ported 2026-09-09
//   6. Creative Elements — ported 2026-09-09
//   7. Prompts       — ported 2026-09-09; wave 3 complete
// v1's `AngleSelectionStep` is NOT deleted and is still mounted by v1's own
// route. It leaves this import because nothing here renders it any more — the
// component itself stays until wave 5 retires v1, per rule 8.
import PsychologyMapStep from "./steps/PsychologyMapStep";
import AngleStep from "./steps/AngleStep";
import HookStep from "./steps/HookStep";
import ScriptStep from "./steps/ScriptStep";
import CtaStep from "./steps/CtaStep";
import ElementsStep from "./steps/ElementsStep";
import PromptsStepV2 from "./steps/PromptsStep";
import TrackSwitcher from "./TrackSwitcher";
import ChatPanel from "./ChatPanel";
import ExplorationScreen from "./ExplorationScreen";
import FormatTypePicker from "../../../components/shared/FormatTypePicker";
import { errorText } from "../../../components/custom_creative_session/DevelopmentStage";
import {
  resolveInitialStep, STEPS, LAST_STEP,
  STEP_PSYCHOLOGY, STEP_ANGLE, STEP_HOOKS, STEP_SCRIPTS,
  STEP_CTA, STEP_ELEMENTS, STEP_PROMPTS,
} from "../../../services/creativeSessionStep";
import * as api from "./api";

/**
 * Workstation · Ads & Creative — **the Autonomous wizard, in v2.**
 *
 * WAVE 2b: THE SHELL. The seven step components are mounted from v1 rather than
 * rewritten, for the reason the Custom session's stages were: they are
 * presentation over rules the server enforces, so a second copy would be a
 * second thing to keep in step. Wave 3 replaces them one at a time BEHIND this
 * shell; until it does, v1 and v2 show the same steps because they are the same
 * components. Their interiors still wear v1 Tailwind — stated, not implied.
 *
 * WHAT IS ACTUALLY PORTED HERE: the step indicator, the navigation, the
 * multi-angle loop, step persistence, and the resume rule.
 *
 * THE RESUME RULE IS SHARED, NOT COPIED. `services/creativeSessionStep` is
 * called by v1 and by this screen. It is the only page-held rule in this
 * workflow, and while both versions run against one database, two
 * implementations would be two answers to a question that has one.
 *
 * ERRORS GO THROUGH `errorText()` — landed here before any step uses it, on the
 * lesson from wave 1: the Custom crash was caused by rewriting a screen's error
 * path, and wave 3 rewrites 23 of them.
 */

const SOURCE_NOTE = {
  stored: null,                       // the normal case; say nothing
  new: null,
  derived:
    "This session had no recorded step, so it was opened from the offer's history.",
  "derived-incomplete":
    "This session had no recorded step, and one lookup failed — the opening step was chosen from incomplete information.",
  fallback:
    "This session had no recorded step and no work to derive one from, so it opened at the beginning.",
  "fallback-incomplete":
    "A lookup failed while working out where to open. This session opened at the beginning, which may not be where you left it.",
};

//: Affiliate-only gate, copied from v1's engine: exploration never shows for ABH.
const ABH_BU_ID = "bu_dcb1830a0871";

export default function AutonomousSessionScreen({ sessionId, offerId, isNewSession, onClose }) {
  const [offer, setOffer] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);   // null = not resolved
  const [state, setState] = useState("loading");          // loading | error | ready
  const [error, setError] = useState(null);
  const [stepSource, setStepSource] = useState(null);
  const [stepWarnings, setStepWarnings] = useState([]);
  // MA-3: the track set is READ, not remembered. Wave 2b kept the angles in
  // React state and rendered "Angle 1 of 3" from it while never advancing —
  // a banner claiming a run the shell did not perform. A track is a real
  // session now, so this asks the backend which sessions the set holds.
  const [tracks, setTracks] = useState({ state: "loading", data: null, error: null });
  const [angleId, setAngleId] = useState(null);
  // The exploration gate, matching v1's condition exactly rather than inventing
  // one: a NEW session with an id, on a non-ABH offer. A resumed session never
  // sees it. The offer arrives asynchronously, so the BU half is applied at
  // render (below) where `offer` is known, not here where it is still null.
  const [showExploration, setShowExploration] = useState(
    () => !!isNewSession && !!sessionId);
  const [refreshKey, setRefreshKey] = useState(0);
  // Only a brand-new session has one; a resumed session is scoped by session_id.
  const [sessionStartTime] = useState(() =>
    isNewSession ? new Date().toISOString() : null);

  const refreshOffer = useCallback(async () => {
    const res = await api.getOffer(offerId);
    if (res.ok) setOffer(res.data);
    return res;
  }, [offerId]);

  useEffect(() => {
    let alive = true;
    const open = async () => {
      setState("loading");
      setError(null);

      const offerRes = await api.getOffer(offerId);
      if (!alive) return;
      if (!offerRes.ok) {
        // The offer is the one thing this screen cannot work without.
        setError(errorText({ response: { data: { detail: offerRes.error } } },
                           "could not load the offer"));
        setState("error");
        return;
      }
      setOffer(offerRes.data);

      const sessionRes = sessionId && !isNewSession
        ? await api.getCreativeSession(sessionId)
        : { ok: true, data: null };
      if (!alive) return;

      const { step, source, warnings } = await resolveInitialStep({
        isNewSession,
        session: sessionRes.ok ? (sessionRes.data?.session || sessionRes.data) : null,
        offer: offerRes.data,
        fetchers: {
          productions: async () => unwrap(await api.getOfferProductions(offerId), "productions"),
          approvedScripts: async () => unwrap(await api.getApprovedUnroutedScripts(offerId), "scripts"),
          scripts: async () => unwrap(await api.getOfferScripts(offerId), "scripts"),
          hooks: async () => unwrap(await api.getOfferHooks(offerId), "hooks"),
        },
      });
      if (!alive) return;

      if (!sessionRes.ok && sessionId && !isNewSession) {
        warnings.unshift(
          `Could not read this session (${sessionRes.error}), so the opening ` +
          `step was worked out from the offer instead of from your last position.`
        );
      }
      setCurrentStep(step);
      setStepSource(source);
      setStepWarnings(warnings);
      setState("ready");
    };
    open();
    return () => { alive = false; };
  }, [offerId, sessionId, isNewSession]);

  /** `{ ok, data }` -> rows, or THROW so the resolver records a failure. */
  const unwrap = (res, key) => {
    if (!res.ok) throw new Error(res.error || "request failed");
    return res.data?.[key] || [];
  };

  // Persist the step the operator is on. Best-effort: a failed write must not
  // block navigation, but it must not be silent either — the next resume will
  // read this value, so losing it changes where they land.
  const persistStep = useCallback(async (step) => {
    if (!sessionId) return;
    const res = await api.setCreativeSessionStep(sessionId, step, STEPS[step]?.label);
    if (!res.ok) {
      console.error("Could not record the current step:", res.error);
      setStepWarnings((w) => [
        ...w.filter((x) => !x.startsWith("Your position")),
        "Your position in this session could not be saved. Reopening it may " +
        "land you somewhere else.",
      ]);
    }
  }, [sessionId]);

  const goToStep = useCallback((step) => {
    const next = Math.max(0, Math.min(step, LAST_STEP));
    setCurrentStep(next);
    persistStep(next);
  }, [persistStep]);

  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = Math.min((prev ?? 0) + 1, LAST_STEP);
      persistStep(next);
      return next;
    });
  }, [persistStep]);

  /** Read this session's track set, and which angle THIS session works on. */
  const loadTracks = useCallback(async () => {
    if (!sessionId) {
      setTracks({ state: "ready", data: { tracks: [], track_total: 1 }, error: null });
      return;
    }
    const res = await api.getSessionTracks(sessionId);
    if (!res.ok) {
      // Not swallowed: an unreadable set and a session that has none are
      // different facts, and the switcher says which.
      setTracks({ state: "error", data: null, error: res.error });
      return;
    }
    setTracks({ state: "ready", data: res.data, error: null });
    const mine = (res.data?.tracks || []).find((t) => t.session_id === sessionId);
    setAngleId(mine?.angle_id || null);
  }, [sessionId]);

  useEffect(() => { loadTracks(); }, [loadTracks, refreshKey]);

  /** The Angle step just cut the set — re-read it so the switcher appears. */
  const handleTracksCreated = useCallback(() => { loadTracks(); }, [loadTracks]);

  // VIDEO FORMAT AND CREATIVE TYPE, on step 1 — the same question a Custom
  // session asks on its first screen.
  //
  // ITS OWN READ, deliberately. The session lookup above runs only when
  // `!isNewSession`, and a BRAND-NEW session is exactly when this question
  // needs asking — so reusing that read would have left the picker blank on
  // the only screen that matters. This one always runs.
  //
  // Added here 2026-09-09 after the Founder found it live: the picker had gone
  // into v1's wizard only. Same shape of mistake as `TrackSwitcher` a few hours
  // earlier — built in one surface, not the other — which is why the option
  // lists and the picker itself are shared modules rather than screen-local.
  const [sessionMeta, setSessionMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let live = true;
    api.getCreativeSession(sessionId).then((res) => {
      if (!live) return;
      if (!res.ok) { setMetaError(res.error); return; }
      setSessionMeta(res.data?.session || res.data);
      setMetaError(null);
    });
    return () => { live = false; };
  }, [sessionId, refreshKey]);

  const saveSessionChoice = useCallback(async (patch) => {
    setSessionMeta((prev) => ({ ...(prev || {}), ...patch }));
    const res = await api.setCreativeSessionMeta(sessionId, patch);
    setMetaError(res.ok ? null : res.error);
  }, [sessionId]);

  /** Open a sibling track. It is a real session, so this is navigation. */
  const openTrack = useCallback((siblingId) => {
    const params = new URLSearchParams(window.location.search);
    params.set("session", siblingId);
    if (offerId) params.set("offer", offerId);
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [offerId]);

  // WAVE 4 — the research / exploration gate.
  //
  // REUSED, NOT REWRITTEN. `components/ExplorationPhase.jsx` was swept and is
  // already correct: one `call()` wrapper that sets a real error state, no
  // swallowed catch, no `alert()`. Rewriting 379 correct lines to restyle them
  // would risk the behaviour to gain appearance, which is not what
  // sweep-then-port is for. It is mounted here, inside v2's shell chrome, and
  // skippable exactly as it is in v1.
  //
  // Affiliate-only, matching v1's gate: ABH has its own engine and never sees
  // this step.
  if (showExploration && sessionId && offer && offer.business_unit_id !== ABH_BU_ID) {
    return (
      /* RESEARCH-EXPLORATION-REDESIGN, 2026-09-10: v2's own screen, which
         carries its own header and stage rail — so the wizard chrome that used
         to wrap it is gone rather than duplicated. v1's `ExplorationPhase` is
         untouched and still mounted by `CreativeEngine.jsx`; both read the same
         state machine through `services/useExploration`... see that module. */
      <ExplorationScreen
        sessionId={sessionId}
        offer={offer}
        onClose={onClose}
        onProceed={() => setShowExploration(false)}
        onSkip={() => setShowExploration(false)}
      />
    );
  }

  if (state === "loading") {
    return (
      <div className="ws-wizard" data-testid="ws-wizard-loading">
        <button type="button" className="btn-ghost btn-sm" onClick={onClose}>
          ← All sessions
        </button>
        <div className="state ws-state">Opening the session…</div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="ws-wizard" data-testid="ws-wizard-error">
        <button type="button" className="btn-ghost btn-sm" onClick={onClose}>
          ← All sessions
        </button>
        <div className="state ws-state state-error" role="alert">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // `angleId` is this track's own angle. The steps pass it to MA-2's opt-in
  // read filter, so a track sees its own hooks and scripts rather than every
  // angle's. Null for a session that is not part of a set — and null means no
  // filter, which is the unchanged behaviour.
  //
  // `sessionMeta` and `onGoToStep` are passed to every step because of the
  // Founder's ruling: a later step must SHOW the choice made on step 1 rather
  // than ask for it again, and when the choice was never made it needs to send
  // the operator back to the one place that asks. Read here, once, rather than
  // re-fetched by each step.
  const stepProps = { offer, sessionId, isNewSession, sessionStartTime,
                      refreshKey, angleId, sessionMeta,
                      onGoToStep: goToStep };
  let content = null;
  if (currentStep === STEP_PSYCHOLOGY) {
    content = <PsychologyMapStep {...stepProps} onComplete={goToNextStep} onRefresh={refreshOffer} />;
  } else if (currentStep === STEP_ANGLE) {
    // WAVE 3 SCREEN 2: v2's own Angle screen. Same hook as v1's step, so the
    // two cannot decide differently; v1 still renders `AngleSelectionStep`.
    content = <AngleStep {...stepProps} onComplete={goToNextStep}
                         onTracksCreated={handleTracksCreated} />;
  } else if (currentStep === STEP_HOOKS) {
    content = <HookStep {...stepProps} onComplete={goToNextStep} />;
  } else if (currentStep === STEP_SCRIPTS) {
    content = <ScriptStep {...stepProps} onComplete={goToNextStep} />;
  } else if (currentStep === STEP_CTA) {
    content = <CtaStep {...stepProps} onComplete={goToNextStep} />;
  } else if (currentStep === STEP_ELEMENTS) {
    content = <ElementsStep {...stepProps} onComplete={goToNextStep} />;
  } else if (currentStep === STEP_PROMPTS) {
    content = <PromptsStepV2 {...stepProps} onFinished={() => setRefreshKey((k) => k + 1)} />;
  }

  const sourceNote = SOURCE_NOTE[stepSource];

  return (
    <div className="ws-wizard" data-testid="ws-wizard">
      <div className="ws-wizard-head">
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={onClose}
          data-testid="ws-wizard-back"
        >
          ← All sessions
        </button>
        <div>
          <h2 className="ws-wizard-title">{offer?.name || "Creative session"}</h2>
          <p className="dim ws-wizard-sub">
            {STEPS[currentStep]?.label} · step {currentStep + 1} of {STEPS.length}
          </p>
        </div>
      </div>

      {/* Where the opening step came from, when it is worth saying. Silent on
          the normal path — a note on every load is a note nobody reads. */}
      {sourceNote ? (
        <p className="dim ws-wizard-note" data-testid="ws-wizard-step-source">{sourceNote}</p>
      ) : null}
      {stepWarnings.length > 0 ? (
        <ul className="ws-wizard-warnings" role="status" data-testid="ws-wizard-warnings">
          {stepWarnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      ) : null}

      <ol className="ws-wizard-steps" data-testid="ws-wizard-indicator">
        {STEPS.map((s, i) => (
          <li key={s.id}>
            <button
              type="button"
              className={`ws-wizard-step ${i === currentStep ? "is-current" : ""} ${i < currentStep ? "is-done" : ""}`}
              onClick={() => goToStep(i)}
              aria-current={i === currentStep ? "step" : undefined}
              data-testid={`ws-wizard-step-${i}`}
            >
              <span className="ws-wizard-step-n">{i + 1}</span>
              <span className="ws-wizard-step-label">{s.label}</span>
            </button>
          </li>
        ))}
      </ol>

      <TrackSwitcher tracks={tracks} currentSessionId={sessionId} onOpen={openTrack} />

      {currentStep === STEP_PSYCHOLOGY && sessionId ? (
        <div className="ws-format-type" data-testid="ws-format-type">
          <p className="ws-format-type-q">How will this creative be made?</p>
          {metaError ? (
            <p className="ws-step-note state-error" role="alert"
               data-testid="ws-format-type-error">{metaError}</p>
          ) : null}
          <FormatTypePicker
            videoFormat={sessionMeta?.video_format}
            creativeType={sessionMeta?.creative_type}
            videoLength={sessionMeta?.video_length}
            formatOptions={sessionMeta?.video_format_options || []}
            typeOptions={sessionMeta?.creative_type_options || []}
            lengthOptions={sessionMeta?.video_length_options || []}
            onFormatChange={(v) => saveSessionChoice({ video_format: v })}
            onTypeChange={(v) => saveSessionChoice({ creative_type: v })}
            onLengthChange={(v) => saveSessionChoice({ video_length: v })}
            testidPrefix="ws-ft"
          />
          <p className="dim ws-format-type-note">
            {/* FOUNDER RULING 2026-09-09: asked once, here, and nowhere else.
                This note used to end "you can still change one on Creative
                Elements" — that is the repetition he asked to be removed. */}
            Chosen once, for this whole session. Every script it writes uses this
            length and this type, and no later step asks again.
          </p>
        </div>
      ) : null}

      {/* WAVE 4: the wizard shipped with no chat panel at all. The step and
          the conversation about the step belong side by side. */}
      <div className="ws-wizard-main">
        <div className="ws-wizard-content" data-testid="ws-wizard-content">
          {content}
        </div>
        <ChatPanel
          sessionId={sessionId}
          currentStep={currentStep}
          stepLabel={STEPS[currentStep]?.label}
          getStepContent={() => ({
            step: STEPS[currentStep]?.label,
            offer_id: offerId,
            angle_id: angleId,
          })}
        />
      </div>

      <div className="ws-wizard-nav">
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => goToStep(currentStep - 1)}
          disabled={currentStep === 0}
          data-testid="ws-wizard-prev"
        >
          Previous
        </button>
        <button
          type="button"
          className="btn-ghost btn-sm"
          onClick={() => goToStep(currentStep + 1)}
          disabled={currentStep === LAST_STEP}
          data-testid="ws-wizard-next"
        >
          Next
        </button>
      </div>
    </div>
  );
}
